import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useSearchParams } from "react-router-dom";
import { Check, Copy, Download, Loader2, RefreshCw, Upload } from "lucide-react";
import { api, apiError, imgUrl } from "@/lib/api";
import { useLocale } from "@/context/LocaleContext";
import { toast } from "sonner";
import { CrosslistSubnav } from "@/components/SellerToolsNav";
import { HIDDEN_SELLER_CHANNELS } from "@/lib/marketplaces";

const STATUS_LABEL = {
  off: "Off",
  ready: "Ready",
  listed: "Listed",
  sold: "Sold — delist",
  delisted: "Delisted",
};

export default function Crosslist() {
  const qc = useQueryClient();
  const { formatMoney } = useLocale();
  const [params, setSearchParams] = useSearchParams();
  const [selected, setSelected] = useState({});
  const [pack, setPack] = useState(null);
  const [busy, setBusy] = useState(false);
  const [importing, setImporting] = useState(false);
  const [q, setQ] = useState(params.get("q") || "");
  const channel = params.get("channel") || "";
  const label = params.get("label") || "";
  const staleOnly = params.get("stale") === "1";

  const { data, isLoading } = useQuery({
    queryKey: ["crosslist-items", q, channel, label, staleOnly],
    queryFn: async () => {
      const p = new URLSearchParams();
      if (q) p.set("q", q);
      if (channel) p.set("channel", channel);
      if (label) p.set("label", label);
      if (staleOnly) p.set("stale", "true");
      return (await api.get(`/seller/crosslist/items?${p}`)).data;
    },
  });

  const items = data?.items || [];
  const channels = (data?.channels || []).filter((c) => !HIDDEN_SELLER_CHANNELS.has(c.id));
  const labels = data?.labels || [];
  const ids = useMemo(() => Object.keys(selected).filter((id) => selected[id]), [selected]);

  const refresh = () => qc.invalidateQueries({ queryKey: ["crosslist-items"] });

  const setFilter = (key, value) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setSearchParams(next);
  };

  const assignLabel = async (labelId, add) => {
    if (!ids.length) return toast.error("Select listings first");
    try {
      await api.post("/seller/labels/assign", { product_ids: ids, add: add ? [labelId] : [], remove: add ? [] : [labelId] });
      toast.success(add ? "Label applied" : "Label removed");
      refresh();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const bulk = async (action, channel) => {
    if (!ids.length) return toast.error("Select listings first");
    setBusy(true);
    try {
      const { data: res } = await api.post("/seller/crosslist/bulk", {
        product_ids: ids,
        action,
        channel: channel || undefined,
      });
      toast.success(`${res.updated} listing${res.updated === 1 ? "" : "s"} updated`);
      setSelected({});
      refresh();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const saveCost = async (p, cost) => {
    try {
      await api.put(`/seller/crosslist/${p.id}`, { cost_of_goods: Number(cost) || 0 });
      refresh();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const openPack = async (p) => {
    try {
      const { data: body } = await api.get(`/seller/crosslist/${p.id}/pack`);
      setPack({ title: p.title, packs: body.packs });
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const copyText = async (text) => {
    await navigator.clipboard.writeText(text);
    toast.success("Copied");
  };

  const exportCsv = async (channel) => {
    try {
      const res = await api.get(`/seller/crosslist/export.csv?channel=${channel}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `shoptosa-${channel}-listings.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const importCsv = async (file) => {
    if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data: res } = await api.post("/seller/crosslist/import.csv", fd);
      toast.success(`Imported ${res.imported} listings as drafts`);
      refresh();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setImporting(false);
    }
  };

  const profitBits = (p) =>
    (p.channel_profits || []).filter((r) => p.channels?.[r.channel]?.status === "listed").slice(0, 4);

  return (
    <div className="max-w-[1200px] mx-auto px-6 lg:px-10 pt-28 pb-24">
      <CrosslistSubnav />
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <div className="text-[11px] tracking-luxe uppercase text-aura-muted mb-2">Crosslist</div>
          <h1 className="text-3xl font-semibold">List once, sell everywhere</h1>
          <p className="text-sm text-aura-muted mt-2 max-w-2xl">
            One master listing plus a form per marketplace. Open an item to edit the Shoptosa form, then eBay, Mercari, and the rest. Connect a shop to authorize it; official APIs post for you, helper shops copy a pack and you click List on their site.
          </p>
        </div>
        <div className="flex gap-2">
          <Link to="/seller/connections" className="px-5 py-2.5 border border-aura-border text-sm rounded-lg">Connections</Link>
          <Link to="/seller/add" className="px-5 py-2.5 bg-aura-accent text-white text-sm rounded-lg">New listing</Link>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <div className="bg-aura-surface border border-aura-border rounded-lg p-5">
          <div className="text-2xl font-mono">{items.length}</div>
          <div className="text-xs text-aura-muted mt-1">Listings</div>
        </div>
        <div className="bg-aura-surface border border-aura-border rounded-lg p-5">
          <div className="text-2xl font-mono">{data?.units ?? "—"}</div>
          <div className="text-xs text-aura-muted mt-1">Units in stock</div>
        </div>
        <div className="bg-aura-surface border border-aura-border rounded-lg p-5">
          <div className="text-2xl font-mono">{data?.sold_flags ?? 0}</div>
          <div className="text-xs text-aura-muted mt-1">Need delist elsewhere</div>
        </div>
        <div className="bg-aura-surface border border-aura-border rounded-lg p-5">
          <div className="text-2xl font-mono">{data?.stale_count ?? 0}</div>
          <div className="text-xs text-aura-muted mt-1">Stale listings</div>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-4">
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onBlur={() => setFilter("q", q)}
          onKeyDown={(e) => e.key === "Enter" && setFilter("q", q)}
          placeholder="Search title or SKU"
          className="px-3 py-1.5 text-xs border border-aura-border rounded-lg bg-aura-surface min-w-[180px]"
        />
        <select value={channel} onChange={(e) => setFilter("channel", e.target.value)} className="px-3 py-1.5 text-xs border border-aura-border rounded-lg bg-aura-surface">
          <option value="">All channels</option>
          {channels.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <select value={label} onChange={(e) => setFilter("label", e.target.value)} className="px-3 py-1.5 text-xs border border-aura-border rounded-lg bg-aura-surface">
          <option value="">All labels</option>
          {labels.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
        </select>
        <button type="button" onClick={() => setFilter("stale", staleOnly ? "" : "1")} className={`px-3 py-1.5 text-xs rounded-lg border ${staleOnly ? "bg-amber-500/20 border-amber-500 text-amber-300" : "border-aura-border"}`}>
          Stale only
        </button>
        {labels.length > 0 && ids.length > 0 && (
          <select onChange={(e) => { if (e.target.value) assignLabel(e.target.value, true); e.target.value = ""; }} className="px-3 py-1.5 text-xs border border-aura-border rounded-lg bg-aura-surface">
            <option value="">Apply label…</option>
            {labels.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
          </select>
        )}
      </div>

      <div className="flex flex-wrap gap-2 mb-6">
        <button disabled={busy} onClick={() => bulk("live")} className="px-3 py-1.5 text-xs rounded-lg bg-aura-accent text-white disabled:opacity-40">Shop live</button>
        <button disabled={busy} onClick={() => bulk("delist")} className="px-3 py-1.5 text-xs rounded-lg border border-aura-border">Delist shop</button>
        <button disabled={busy} onClick={() => bulk("relist")} className="px-3 py-1.5 text-xs rounded-lg border border-aura-border flex items-center gap-1">
          <RefreshCw className="w-3 h-3" /> Relist (new date)
        </button>
        <button disabled={busy} onClick={() => bulk("mark_listed", "ebay")} className="px-3 py-1.5 text-xs rounded-lg border border-aura-border">Mark eBay listed</button>
        <button onClick={() => exportCsv("generic")} className="px-3 py-1.5 text-xs rounded-lg border border-aura-border flex items-center gap-1">
          <Download className="w-3 h-3" /> CSV
        </button>
        <button onClick={() => exportCsv("ebay")} className="px-3 py-1.5 text-xs rounded-lg border border-aura-border">eBay CSV</button>
        <button onClick={() => exportCsv("etsy")} className="px-3 py-1.5 text-xs rounded-lg border border-aura-border">Etsy CSV</button>
        <button onClick={() => exportCsv("amazon")} className="px-3 py-1.5 text-xs rounded-lg border border-aura-border">Amazon CSV</button>
        <button onClick={() => exportCsv("walmart")} className="px-3 py-1.5 text-xs rounded-lg border border-aura-border">Shopify CSV</button>
        <label className="px-3 py-1.5 text-xs rounded-lg border border-aura-border cursor-pointer flex items-center gap-1">
          {importing ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
          Import CSV
          <input type="file" accept=".csv,text/csv" className="hidden" onChange={(e) => importCsv(e.target.files?.[0])} />
        </label>
      </div>

      {isLoading ? (
        <div className="space-y-3">{Array.from({ length: 4 }).map((_, i) => <div key={i} className="h-24 shimmer rounded-lg" />)}</div>
      ) : items.length === 0 ? (
        <div className="py-16 text-center text-aura-muted">
          No listings yet. Ingest from Closet or import a CSV with a Title column.
        </div>
      ) : (
        <div className="bg-aura-surface border border-aura-border rounded-lg divide-y divide-aura-border" data-testid="crosslist-table">
          {items.map((p) => (
            <div key={p.id} className="p-4">
              <div className="flex items-start gap-3">
                <input
                  type="checkbox"
                  checked={!!selected[p.id]}
                  onChange={(e) => setSelected((s) => ({ ...s, [p.id]: e.target.checked }))}
                  className="mt-2"
                />
                <Link to={`/seller/crosslist/${p.id}`} className="w-14 h-18 bg-aura-sand overflow-hidden rounded shrink-0">
                  <img src={imgUrl(p.images?.[0])} alt="" className="w-full h-full object-cover" />
                </Link>
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">
                    {p.stale && <span className="text-amber-400 mr-1" title={`${p.age_days} days`}>⚠</span>}
                    <Link to={`/seller/crosslist/${p.id}`}>{p.title}</Link>
                  </div>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {(p.labels || []).map((l) => (
                      <span key={l.id} className="text-[10px] px-1.5 py-0.5 rounded-full" style={{ background: l.color, color: "#fff" }}>{l.name}</span>
                    ))}
                  </div>
                  <div className="text-xs text-aura-muted font-mono mt-0.5">
                    {formatMoney(p.price, p.currency || "USD", { convert: false })} · {p.quantity ?? 0} in stock
                    {p.sku ? ` · SKU ${p.sku}` : ""}
                    {p.asin ? ` · ASIN ${p.asin}` : ""}
                    {p.upc || p.gtin ? ` · UPC ${p.upc || p.gtin}` : ""}
                    {p.fsn ? ` · FSN ${p.fsn}` : ""}
                    {p.needs_delist?.length ? ` · delist on ${p.needs_delist.join(", ")}` : ""}
                  </div>
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {channels.map((c) => {
                      const st = p.channels?.[c.id]?.status || "off";
                      const shown = c.id === "walmart" ? "shopify" : c.id;
                      const to = c.id === "shoptosa" ? `/seller/crosslist/${p.id}` : `/seller/crosslist/${p.id}?channel=${shown}`;
                      return (
                        <Link
                          key={c.id}
                          to={to}
                          title={c.blurb}
                          className={`text-[10px] px-2 py-0.5 rounded-full border ${
                            st === "listed" ? "bg-aura-accent text-white border-aura-accent"
                              : st === "sold" ? "border-red-400 text-red-500"
                              : st === "ready" ? "border-aura-accent text-aura-accent"
                              : "border-aura-border text-aura-muted"
                          }`}
                        >
                          {c.name} · {STATUS_LABEL[st] || st}
                        </Link>
                      );
                    })}
                  </div>
                </div>
                <label className="text-[10px] tracking-luxe uppercase text-aura-muted w-24 shrink-0">
                  Cost
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    defaultValue={p.cost_of_goods || 0}
                    onBlur={(e) => {
                      if (Number(e.target.value) !== Number(p.cost_of_goods || 0)) saveCost(p, e.target.value);
                    }}
                    className="mt-1 w-full px-2 py-1.5 border border-aura-border outline-none text-sm font-mono rounded bg-aura-surface"
                  />
                </label>
                <button type="button" onClick={() => openPack(p)} className="text-xs px-3 py-1.5 border border-aura-border rounded-lg flex items-center gap-1 shrink-0">
                  <Copy className="w-3 h-3" /> Packs
                </button>
              </div>
              {profitBits(p).length ? (
                <div className="pl-9 mt-2 text-[11px] text-aura-muted font-mono">
                  Est. profit after fees
                  {profitBits(p).map((r) => (
                    <span key={r.channel} className="ml-3">{r.name} ${r.est_profit}</span>
                  ))}
                </div>
              ) : null}
            </div>
          ))}
        </div>
      )}

      {pack && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-end sm:items-center justify-center p-4" onClick={() => setPack(null)}>
          <div className="bg-aura-surface border border-aura-border rounded-lg max-w-2xl w-full max-h-[80vh] overflow-auto p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <h2 className="font-semibold">Listing packs — {pack.title}</h2>
              <button type="button" onClick={() => setPack(null)} className="text-sm text-aura-muted">Close</button>
            </div>
            {Object.entries(pack.packs || {}).map(([cid, body]) => (
              <div key={cid} className="mb-5">
                <div className="text-[11px] tracking-luxe uppercase text-aura-muted mb-1">{cid}</div>
                <div className="text-sm font-medium">{body.title}</div>
                <p className="text-xs text-aura-muted mt-1 whitespace-pre-wrap">{(body.description || "").slice(0, 400)}</p>
                <button type="button" onClick={() => copyText(`${body.title}\n\n${body.description || ""}`)} className="mt-2 text-xs px-3 py-1 border border-aura-border rounded-lg flex items-center gap-1">
                  <Check className="w-3 h-3" /> Copy {cid}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
